/**
 * 429 (Too Many Requests) handling shared by apiClient and the XHR upload path.
 *
 * The backend sends Retry-After with every 429, in seconds. An HTTP date is
 * accepted too, since the header allows either.
 */

/** Seconds until the limit resets, or null when the header is missing or unreadable. */
export function parseRetryAfter(retryAfter: string | null): number | null {
  if (!retryAfter) return null;
  const value = retryAfter.trim();
  if (/^\d+$/.test(value)) return Number(value);
  const date = Date.parse(value);
  if (Number.isNaN(date)) return null;
  return Math.max(0, Math.round((date - Date.now()) / 1000));
}

function messageForSeconds(seconds: number | null): string {
  if (seconds === null) return 'Too many requests. Try again later.';
  const minutes = Math.max(1, Math.ceil(seconds / 60));
  return `Too many requests. Try again in ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}.`;
}

/**
 * Thrown for a 429 so callers can tell it apart (for example to pause and
 * retry). Its message is what the user is shown, from Retry-After.
 */
export class RateLimitError extends Error {
  readonly retryAfterSeconds: number | null;

  constructor(retryAfter: string | null) {
    const seconds = parseRetryAfter(retryAfter);
    super(messageForSeconds(seconds));
    this.name = 'RateLimitError';
    this.retryAfterSeconds = seconds;
  }
}
